import { Injectable, inject } from '@angular/core';

import {
  DatapointFieldColumn,
  ExtractionColumn,
} from 'projects/semeris/src/generated/scalars/scalars';
import { merge, Observable } from 'rxjs';
import { finalize, map, publish, refCount } from 'rxjs/operators';

import { GET_FORMULA_BY_ID } from '../../formula/constants/formula.queries';
import { DatapointSourceType } from '../../formula/models/formula.model';
import { WebSocketEntities } from '../../shared/models/web-socket/web-socket.models';
import { GraphQLService } from '../../shared/services/graphql.service';
import { WebSocketService } from '../../shared/services/web-socket/web-socket.service';
import { arrayToRecord } from '../../shared/utils/array-to-record';
import { GET_LOOKUP, GET_TOPIC_LOOKUP_BY_ID } from '../constants/gql-queries';
import {
  DatapointLookupCollection,
  DatapointLookupResponse,
  FormulaLookupEntry,
  TopicLookupEntry,
} from '../models/datapoint-lookup-collection.model';

@Injectable({ providedIn: 'root' })
export class DatapointLookupService {
  private readonly graphQlService = inject(GraphQLService);
  private readonly wsService = inject(WebSocketService);

  private wsListener$: Observable<any> = new Observable<{
    type: WebSocketEntities;
    message: string;
  }>((observer) => {
    observer.add(
      merge(
        this.wsService
          .subscribe(WebSocketEntities.Topic, undefined, { debounceTime: 0, ignoreTabId: true })
          .pipe(
            map((message) => ({
              type: WebSocketEntities.Topic,
              message,
            })),
            finalize(() => {
              this.wsService.unsubscribe(WebSocketEntities.Topic);
            })
          ),
        this.wsService
          .subscribe(WebSocketEntities.Formula, undefined, { debounceTime: 0, ignoreTabId: true })
          .pipe(
            map((message) => ({
              type: WebSocketEntities.Formula,
              message,
            })),
            finalize(() => {
              this.wsService.unsubscribe(WebSocketEntities.Formula);
            })
          )
      ).subscribe(observer)
    );
  }).pipe(publish(), refCount());

  public getLookup(): Observable<DatapointLookupCollection> {
    return this.graphQlService
      .getRawQuery<DatapointLookupResponse>(GET_LOOKUP)
      .pipe(map((response) => this.responseToCollection(response)));
  }

  public getTopicLookupEntry(id: string): Observable<DatapointLookupCollection> {
    return this.graphQlService
      .getRawQuery<TopicLookupEntry>(GET_TOPIC_LOOKUP_BY_ID, {
        id,
      })
      .pipe(
        map((topicEntry) =>
          this.responseToCollection({
            topics: [topicEntry],
            formulas: [],
          })
        )
      );
  }

  public getFormulaLookupEntry(id: string): Observable<DatapointLookupCollection> {
    return this.graphQlService
      .getRawQuery<FormulaLookupEntry>(GET_FORMULA_BY_ID, {
        id,
      })
      .pipe(
        map((formulaEntry) =>
          this.responseToCollection({
            topics: [],
            formulas: [formulaEntry],
          })
        )
      );
  }

  public listenOnChanges$(): Observable<{ type: WebSocketEntities; message: string }> {
    return this.wsListener$;
  }

  private responseToCollection(response: DatapointLookupResponse): DatapointLookupCollection {
    return {
      formulaReferences: response.formulas.reduce((acc, formula) => {
        for (const variable of formula.variables) {
          if (
            variable.value &&
            (variable.type === DatapointSourceType.DatapointField ||
              variable.type === DatapointSourceType.DatapointRule)
          ) {
            acc[variable.value] = acc[variable.value] || [];
            acc[variable.value].push(formula);
          }
        }

        return acc;
      }, {}),
      formulaById: arrayToRecord(response.formulas, '_id'),
      topics: response.topics.reduce((acc, topic) => {
        for (const value of topic.values) {
          switch (value.type) {
            case 'field': {
              const datapointFieldColumn = value as DatapointFieldColumn;
              acc[datapointFieldColumn.datapointFieldId] =
                acc[datapointFieldColumn.datapointFieldId] || [];
              acc[datapointFieldColumn.datapointFieldId].push(topic);
              break;
            }
            case 'primitiveExtractionRule':
            case 'extractionRule':
            case 'sectionExtraction': {
              const sectionExtractionColumn = value as ExtractionColumn;
              acc[sectionExtractionColumn.datapointRuleId] =
                acc[sectionExtractionColumn.datapointRuleId] || [];
              acc[sectionExtractionColumn.datapointRuleId].push(topic);
              break;
            }
          }
        }

        return acc;
      }, {}),
    } as DatapointLookupCollection;
  }
}
